export interface PendingTax {
    tax_name?: number;          // regime ID
    effective_date?: string;    // YYYY-MM-DD
    age?: string;               // "18 - 60"
    gender?: string;            // Male | Female | Both | Others
    min_amount?: number;
    max_amount?: number;
    tax_percentage?: number;
    description?: string;
    status?: string;            // active | inactive
}

export interface PendingDeleteTax {
    awaitingDescription?: boolean;
}

export const startCreateTaxFlow = (
    setPending: (data: PendingTax | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    setPending({});
    pushAIChat("Sure! Which tax regime should this slab belong to?");
    speak("Which tax regime should this slab belong to?");
    window.dispatchEvent(
        new CustomEvent("ai-suggest-options", {
            detail: { type: "tds-regime" }
        })
    );
};

export const processPendingTax = (
    text: string,
    pending: PendingTax,
    setPending: (data: PendingTax | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — TAX REGIME ID
    if (!pending.tax_name) {
        const regimeId = Number(text.replace(/[^0-9]/g, ""));

        if (!regimeId) {
            pushAIChat("Please provide a valid tax regime ID.");
            speak("Invalid tax regime.");
            return;
        }

        setPending({ ...pending, tax_name: regimeId });

        pushAIChat("What is the effective date? (YYYY-MM-DD)");
        speak("What is the effective date?");
        return;
    }

    // STEP 2 — EFFECTIVE DATE
    if (!pending.effective_date) {
        const date = new Date(text);

        if (isNaN(date.getTime())) {
            pushAIChat("Please provide a valid effective date.");
            speak("Invalid effective date.");
            return;
        }

        setPending({
            ...pending,
            effective_date: date.toISOString().split("T")[0]
        });

        pushAIChat("Enter the age range (for example: 18 - 60).");
        speak("What is the age range?");
        return;
    }

    // STEP 3 — AGE RANGE
    if (!pending.age) {
        if (!text.includes("-")) {
            pushAIChat("Please provide a valid age range like 18 - 60.");
            speak("Invalid age range.");
            return;
        }

        setPending({ ...pending, age: text.trim() });

        pushAIChat("What gender does this slab apply to? Male, Female, Both, or Others?");
        speak("What gender does this slab apply to?");
        return;
    }

    // STEP 4 — GENDER
    if (!pending.gender) {
        const raw = text.trim().toLowerCase();

        const genderMap: Record<string, "Male" | "Female" | "Both" | "Others"> = {
            male: "Male",
            female: "Female",
            both: "Both",
            others: "Others",
            other: "Others"
        };

        const gender = genderMap[raw];

        if (!gender) {
            pushAIChat("Gender must be Male, Female, Both, or Others.");
            speak("Invalid gender.");
            return;
        }

        setPending({ ...pending, gender });

        pushAIChat("What is the minimum amount?");
        speak("What is the minimum amount?");
        return;
    }

    // STEP 5 — MIN AMOUNT
    if (pending.min_amount === undefined) {
        const min = Number(text.replace(/[^0-9]/g, ""));

        if (isNaN(min)) {
            pushAIChat("Please provide a valid minimum amount.");
            speak("Invalid minimum amount.");
            return;
        }

        setPending({ ...pending, min_amount: min });

        pushAIChat("What is the maximum amount? (You can say 0 if not applicable)");
        speak("What is the maximum amount?");
        return;
    }

    // STEP 6 — MAX AMOUNT
    if (pending.max_amount === undefined) {
        const max = Number(text.replace(/[^0-9]/g, ""));

        setPending({ ...pending, max_amount: max || 0 });

        pushAIChat("What is the tax percentage?");
        speak("What is the tax percentage?");
        return;
    }

    // STEP 7 — TAX PERCENTAGE
    if (!pending.tax_percentage) {
        const percent = Number(text.replace(/[^0-9.]/g, ""));

        if (!percent || percent < 0 || percent > 100) {
            pushAIChat("Please provide a valid tax percentage between 0 and 100.");
            speak("Invalid tax percentage.");
            return;
        }

        setPending({ ...pending, tax_percentage: percent });

        pushAIChat("Any description for this TDS slab?");
        speak("Any description?");
        return;
    }

    // STEP 8 — DESCRIPTION
    if (!pending.description) {
        setPending({ ...pending, description: text });

        pushAIChat("Should this slab be active or inactive?");
        speak("Should this slab be active or inactive?");
        return;
    }

    // STEP 9 — STATUS (FINAL)
    if (!pending.status) {
        const status = text.toLowerCase().includes("inactive") ? "inactive" : "active";

        pushAIChat("Creating TDS slab...");
        speak("Creating TDS slab.");

        window.dispatchEvent(
            new CustomEvent("ai-create-tax", {
                detail: {
                    ...pending,
                    status
                }
            })
        );

        setPending(null);
        return;
    }
};

export const startDeleteTaxFlow = (
    setPending: (data: PendingDeleteTax | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    descriptionFromIntent?: string
) => {
    if (descriptionFromIntent) {
        pushAIChat(`Deleting TDS slab "${descriptionFromIntent}"...`);
        speak(`Deleting TDS slab ${descriptionFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-tax", {
                detail: { description: descriptionFromIntent }
            })
        );
        return;
    }

    setPending({ awaitingDescription: true });
    pushAIChat("Which TDS slab would you like to delete?");
    speak("Which TDS slab would you like to delete?");
};

export const processPendingDeleteTax = (
    text: string,
    pending: PendingDeleteTax,
    setPending: (data: PendingDeleteTax | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingDescription) return;

    const description = text.trim();

    if (!description) {
        pushAIChat("Please provide a valid TDS slab description.");
        speak("Invalid description.");
        return;
    }

    pushAIChat(`Deleting TDS slab "${description}"...`);
    speak(`Deleting TDS slab ${description}`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-tax", {
            detail: { description }
        })
    );

    setPending(null);
};
